import React, { createContext, useContext, useEffect, useState } from "react";
import io from "socket.io-client";
import { useAuth } from "./AuthContext";
import axios from "axios";

// Create the context
const NotificationContext = createContext();

// Create the provider
export const NotificationProvider = ({ children }) => {
    const { accessToken, isAuthenticated } = useAuth();
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Use environment variable for socket URL or default to localhost:5003
    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://localhost:5003";
    const baseURL = process.env.REACT_APP_BASEURL;

    // Initialize Socket.io connection
    useEffect(() => {
        if (isAuthenticated && accessToken) {
            // Connect to the socket server
            const newSocket = io(SOCKET_URL, {
                auth: {
                    token: accessToken,
                },
                transports: ["websocket"], // Use websocket transport for better performance
            });

            setSocket(newSocket);

            // Listen for connection events
            newSocket.on("connect", () => {
                console.log("Connected to notification service");
            });

            newSocket.on("disconnect", () => {
                console.log("Disconnected from notification service");
            });

            newSocket.on("connect_error", (err) => {
                console.error("Socket connection error:", err.message);
            });

            // Listen for new notifications
            newSocket.on("new_notification", (notification) => {
                console.log("New notification received:", notification);
                setNotifications((prev) => [notification, ...prev]);
                setUnreadCount((prev) => prev + 1);
            });

            // Cleanup on unmount or when auth changes
            return () => {
                newSocket.disconnect();
            };
        }
    }, [isAuthenticated, accessToken, SOCKET_URL]);

    // Function to fetch initial notifications (sync)
    const syncNotifications = async () => {
        if (!accessToken) return;

        try {
            const response = await axios.get(`${baseURL}/notifications`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            const fetchedNotifications = response.data.data || [];
            setNotifications(fetchedNotifications);

            // Calculate unread count
            const count = fetchedNotifications.filter(n => !n.is_read).length;
            setUnreadCount(count);

        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    };

    // Initial sync when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            syncNotifications();
        }
    }, [isAuthenticated, accessToken]);

    // Function to mark a notification as read
    const markAsRead = async (id) => {
        try {
            // Optimistic update
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));

            // API call to mark as read
            await axios.put(
                `${baseURL}/notifications/${id}/read`,
                {},
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }
            );
        } catch (error) {
            console.error("Error marking notification as read:", error);
            // Revert if failed (optional, but good practice)
            syncNotifications();
        }
    };

    // Function to mock a notification for testing
    const testNotification = () => {
        const mockNotification = {
            id: Date.now(),
            message: "This is a test notification",
            is_read: false,
            created_at: new Date().toISOString(),
            type: "info"
        };
        setNotifications((prev) => [mockNotification, ...prev]);
        setUnreadCount((prev) => prev + 1);
    };

    const clearAll = async () => {
        // Implementation depends on backend capabilities, 
        // for now we can just clear local state or loop through unread
        // A dedicated 'mark all read' endpoint is recommended.

        // For this plan, let's just mark all visible as read locally for UI responsiveness
        // and maybe trigger individual calls or a bulk call if available.
        // Assuming a bulk endpoint doesn't exist yet based on description, 
        // we will iterate or just visually clear.

        const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);

        // Optimistic
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);

        // Attempt to mark all as read on server
        try {
            await Promise.all(unreadIds.map(id =>
                axios.put(
                    `${baseURL}/notifications/${id}/read`,
                    {},
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                )
            ));
        } catch (err) {
            console.error("Error clearing notifications", err);
        }
    };

    return (
        <NotificationContext.Provider
            value={{
                notifications,
                unreadCount,
                markAsRead,
                syncNotifications,
                testNotification,
                clearAll
            }}
        >
            {children}
        </NotificationContext.Provider>
    );
};

// Custom hook to use the context
export const useNotification = () => useContext(NotificationContext);
